// Stuck-step reporting for async chains — the mutation-chain counterpart to the
// database layer's `trackQuery`.
//
// The DB watchdog only sees SQLite round trips. But a rating chain awaits several
// things that are NOT queries:
//
//   persistentKeyboardPreserveFocus()   → a native turbo-module call
//   persistStudyCardReview()            → a transaction (DB-visible)
//   resolveLearnCardDestination()       → fetchQuery + navigator
//     └─ navigator `prepareQueries`     → sequential fetchQuery, NO timeout
//
// Any of those can hang without a single slow SQL statement: `fetchQuery` adopts
// an already-in-flight promise for the same key, so a wedged fetch parks every
// later awaiter forever. When that happens the rating mutation's `isPending`
// never clears and the module-level rating latch is never released, which is why
// every rating path freezes while the rest of the app stays browsable.
//
// Same two edges as the DB watchdog, for the same reason — a step that never
// settles never reaches its own completion handler, so the report has to be armed
// at START, not derived on finish.

import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import { PERF_OP, recordPerfDuration } from "@-/dev.improve.haniverse.lib.sentry/perf";

/**
 * How long a single step in an async chain may run before it is anomalous. Higher
 * than the per-query threshold: a step legitimately aggregates several queries
 * plus a render, so one second here would be noise rather than signal.
 */
export const SLOW_STEP_THRESHOLD_MS = 3000;

/**
 * Escalations after the first report, so a permanently-wedged step proves it never
 * returned without spamming forever. Bounded — see the DB watchdog's equivalent.
 */
const STUCK_REESCALATION_MS = [10_000, 60_000] as const;

const STUCK_FINGERPRINT = "async-step-stuck";

export interface AsyncStepReporter {
  onSlowCompletion: (
    stepName: string,
    durationMs: number,
    outcome: "resolved" | "rejected",
  ) => void;
  onStuck: (stepName: string, elapsedMs: number) => void;
}

function reportStuckStep(stepName: string, elapsedMs: number): void {
  const error = new Error(`Async step still running after ${elapsedMs}ms: ${stepName}`);
  error.name = "StuckStepWatchdog";

  captureSentryException(error, {
    contexts: {
      "han.step": { elapsedMs, state: "in-flight", step: stepName },
    },
    // Group by step name only: the elapsed figure lives in the message and would
    // otherwise split one recurring hang across many issues.
    fingerprint: [STUCK_FINGERPRINT, stepName],
    level: "error",
    tags: { "han.step.name": stepName, "han.step.stuck": "true" },
  });
}

function reportSlowStepCompletion(
  stepName: string,
  durationMs: number,
  outcome: "resolved" | "rejected",
): void {
  recordPerfDuration(
    `slow step ${stepName}`,
    {
      attributes: {
        "han.step.elapsed_ms": durationMs,
        "han.step.name": stepName,
        "han.step.outcome": outcome,
      },
      op: PERF_OP.db,
    },
    durationMs,
  );
}

export const defaultAsyncStepReporter: AsyncStepReporter = {
  onSlowCompletion: reportSlowStepCompletion,
  onStuck: reportStuckStep,
};

/**
 * Run `step` under a stuck-watchdog.
 *
 * Returns the step's own result untouched and rethrows its error unchanged — this
 * is pure observation, so wrapping can never alter behaviour. It deliberately does
 * NOT time out the step: converting a hang into a rejection would change what the
 * user sees (and would mask the bug we are trying to observe). It only reports.
 */
export async function watchAsyncStep<T>(
  stepName: string,
  step: () => Promise<T>,
  reporter: AsyncStepReporter = defaultAsyncStepReporter,
): Promise<T> {
  const startTime = Date.now();
  let settled = false;

  const timers: ReturnType<typeof setTimeout>[] = [];
  const armEscalation = (delayMs: number): void => {
    timers.push(
      setTimeout(() => {
        if (settled) {
          return;
        }
        try {
          reporter.onStuck(stepName, Date.now() - startTime);
        } catch {
          // Instrumentation must never surface as a step failure.
        }
      }, delayMs),
    );
  };

  armEscalation(SLOW_STEP_THRESHOLD_MS);
  for (const extraDelayMs of STUCK_REESCALATION_MS) {
    armEscalation(SLOW_STEP_THRESHOLD_MS + extraDelayMs);
  }

  const settle = (outcome: "resolved" | "rejected"): void => {
    if (settled) {
      return;
    }
    settled = true;
    for (const timer of timers) {
      clearTimeout(timer);
    }
    const durationMs = Date.now() - startTime;
    if (durationMs < SLOW_STEP_THRESHOLD_MS) {
      return;
    }
    try {
      reporter.onSlowCompletion(stepName, durationMs, outcome);
    } catch {
      // As above.
    }
  };

  try {
    const result = await step();
    settle("resolved");
    return result;
  } catch (error) {
    settle("rejected");
    throw error;
  }
}
